import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGame } from '../context/GameContext';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import MobileTopBarClean from '../components/MobileTopBarClean';
import AchievementSystem from '../components/AchievementSystem';
import { useOnboarding } from '../context/OnboardingContext';
import '../components/MobileLayout.css';
import transactionService from '../services/transactionService';
import '../styles/wired-sidebar-dashboard.css';
import '../styles/modern-dashboard-overlay.css';
import '../styles/jpx-dashboard.css';
import '../styles/global-white-backgrounds.css';
import '../styles/professional-investment-cards.css';
import '../styles/mobile-overrides.css';
import '../styles/skate-streetwear.css';
import '../styles/dashboard-animations.css';
import '../styles/red-button-override.css';
import '../styles/enhanced-onboarding.css';
import mainLogo from '../assets/images/mainlogo.png';
import chessKingIcon from '../assets/images/chess-king.png';
import gideonBullIcon from '../assets/images/gideon-bull-icon.png';
import GideonAssessmentModal from '../components/GideonAssessmentModal';
import AIModeSwitch from '../components/AIModeSwitch';
import FloatingCompetitionButton from '../components/FloatingCompetitionButton';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';



const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { loadPortfolioData, gameState } = useGame();
  const { getHeaderStats } = usePortfolioStats();
  const { t } = useTranslation();
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [loanAmount, setLoanAmount] = useState('');
  const [acceptedLoan, setAcceptedLoan] = useState(gameState.loan > 0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loanLoading, setLoanLoading] = useState(false);
  const [loanError, setLoanError] = useState(null);
  const [repayAmount, setRepayAmount] = useState('');
  const [repayLoading, setRepayLoading] = useState(false);
  const [repayError, setRepayError] = useState(null);
  const [showRepayModal, setShowRepayModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [depositError, setDepositError] = useState(null);
  const [withdrawError, setWithdrawError] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const isMobile = useIsMobile();
  const [showTradePopup, setShowTradePopup] = useState(false);
  const [isDollarBillFlipped, setIsDollarBillFlipped] = useState(false);
  // Note: onboardingCompleted state removed - now using hasCompletedOnboarding from OnboardingContext
  const [expandedAssets, setExpandedAssets] = useState(new Set());
  const [showGideonAssessment, setShowGideonAssessment] = useState(false);

  // Onboarding flow state
  const { startOnboardingFromChat, hasCompletedOnboarding, personalityProfile, resetPersonalityProfile, isInitialLoading } = useOnboarding();
  const [onboardingContext, setOnboardingContext] = useState(null);
  const [showProfileCompleteModal, setShowProfileCompleteModal] = useState(false);


  // Function to check onboarding completion status
  const checkOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('userToken'); // Fixed: use 'userToken' not 'token'
      console.log('📊 Checking onboarding status...', { hasToken: !!token });
      
      if (!token) {
        console.log('🚫 No token found, keeping onboardingCompleted as false');
        return;
      }
      
      const response = await fetch('/api/onboarding/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📊 Onboarding API response:', { status: response.status, ok: response.ok });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Onboarding data:', data);
        // Note: onboardingCompleted state removed - now using hasCompletedOnboarding from OnboardingContext
        console.log('📊 Onboarding data received:', data.onboardingCompleted || false);
      } else {
        console.log('📊 API response not ok, keeping default false');
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      // Keep default false to allow flip functionality when API fails
      console.log('Keeping onboardingCompleted as false due to API error');
    }
  };

  // Load portfolio data and check onboarding status on component mount
  useEffect(() => {
    const loadData = async () => {
      setPortfolioLoading(true);
      try {
        await loadPortfolioData();
        await checkOnboardingStatus();
      } finally {
        setPortfolioLoading(false);
      }
    };
    
    if (user) {
      loadData();
    }
  }, [user]); // Removed loadPortfolioData dependency to prevent frequent calls
  
  // Check if user just completed signup/onboarding
  useEffect(() => {
    const justCompletedSignup = sessionStorage.getItem('justCompletedSignup');
    if (justCompletedSignup === 'true' && !isMobile) {
      setShowTradePopup(true);
      // Clear the flag after showing
      sessionStorage.removeItem('justCompletedSignup');
    }
  }, [isMobile]);



  // Update accepted loan state based on gameState
  useEffect(() => {
    setAcceptedLoan(gameState.loan > 0);
  }, [gameState.loan]);

  // Handle onboarding flow trigger events
  useEffect(() => {
    const handleOpenOnboardingFlow = (event) => {
      const { source, context } = event.detail || {};

      console.log('🎯 Opening onboarding flow from:', source, 'with context:', context);
      console.log('🚀 Navigating to onboarding page...');

      setShowGideonAssessment(false); // Close the Gideon modal

      // Navigate to onboarding page with context
      navigate('/onboarding', {
        state: {
          context: context,
          source: source || 'dashboard'
        }
      });
    };

    window.addEventListener('openOnboardingFlow', handleOpenOnboardingFlow);
    
    return () => {
      window.removeEventListener('openOnboardingFlow', handleOpenOnboardingFlow);
    };
  }, [startOnboardingFromChat]);

  // Auto-flip dollar bill for users who haven't completed onboarding
  useEffect(() => {
    // Don't auto-flip while still loading
    if (isInitialLoading) {
      console.log('🔄 Auto-flip blocked: still loading onboarding status');
      return;
    }
    
    // Auto-flip to show Gideon banner for users who haven't completed onboarding
    if (!hasCompletedOnboarding && !isDollarBillFlipped) {
      console.log('🎯 Auto-flipping dollar bill for non-onboarded user');
      setIsDollarBillFlipped(true);
    } else if (hasCompletedOnboarding && isDollarBillFlipped) {
      console.log('✅ User completed onboarding, flipping back to dollar bill');
      setIsDollarBillFlipped(false);
    }
  }, [hasCompletedOnboarding, isInitialLoading, isDollarBillFlipped]);

  // Auto-flip dollar bill when user scrolls TO "Grow Your Wealth" section (only if onboarding not completed)
  useEffect(() => {
    const handleScroll = () => {
      // Don't flip while still loading
      if (isInitialLoading) {
        console.log('🔄 Flip blocked: still loading onboarding status');
        return;
      }
      
      // Scroll-based flipping is for non-onboarded users as additional trigger
      // Completed users don't need scroll-based flipping
      if (hasCompletedOnboarding) {
        console.log('🚫 Scroll flip disabled: onboarding completed');
        return;
      }
      
      const growYourWealthElement = document.getElementById('grow-your-wealth-section');
      
      if (growYourWealthElement) {
        const elementRect = growYourWealthElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if the top of "Grow Your Wealth" title is at or above the top of the viewport
        // This means user has scrolled down to or past the title
        const hasScrolledToTitle = elementRect.top <= 0;
        
        console.log('💵 Scroll check:', {
          elementTop: elementRect.top,
          windowHeight,
          hasScrolledToTitle,
          isDollarBillFlipped
        });
        
        // Flip when scrolled down to title, flip back when scrolled above it
        if (hasScrolledToTitle && !isDollarBillFlipped) {
          console.log('🔄 Triggering dollar bill flip! (scrolled to title)');
          setIsDollarBillFlipped(true);
        } else if (!hasScrolledToTitle && isDollarBillFlipped) {
          console.log('🔄 Flipping back dollar bill! (scrolled above title)');
          setIsDollarBillFlipped(false);
        }
      } else {
        console.log('❌ grow-your-wealth-section element not found');
      }
    };

    // Add scroll listener with throttling for performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    // Check initial position
    handleScroll();
    
    // Cleanup
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [hasCompletedOnboarding, isInitialLoading]);

  // Toggle expanded state for asset details
  const toggleAssetExpansion = (assetName) => {
    setExpandedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetName)) {
        newSet.delete(assetName);
      } else {
        newSet.add(assetName);
      }
      return newSet;
    });
    
    // Haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
  };


  const handleLoanAccept = async () => {
    const amount = parseFloat(loanAmount);
    if (amount > 0 && amount <= 1000) {
      setLoanLoading(true);
      setLoanError(null);
      
      try {
        // Call backend API to take loan
        const userId = user._id || localStorage.getItem('userId');
        const token = localStorage.getItem('userToken');
        
        console.log('=== LOAN REQUEST DEBUG ===');
        console.log('User ID:', userId);
        console.log('Token exists:', !!token);
        console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
        console.log('Amount:', amount);
        console.log('API URL:', process.env.REACT_APP_API_URL || 'http://localhost:8000');
        
        if (!userId) {
          throw new Error('User ID not found. Please log in again.');
        }
        
        if (!token) {
          throw new Error('Authentication token not found. Please log in again.');
        }
        
        const response = await transactionService.takeLoan(userId, amount);
        console.log('Loan response:', response);
        
        if (response.success) {
          // Refresh portfolio data from API to update GameContext
          await loadPortfolioData();
          
          setAcceptedLoan(true);
          setShowLoanModal(false);
          setLoanAmount('');
        } else {
          throw new Error(response.message || 'Loan request was not successful');
        }
      } catch (error) {
        console.error('=== LOAN ERROR DEBUG ===');
        console.error('Full error:', error);
        console.error('Error response:', error.response);
        console.error('Error data:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        let errorMessage = 'Failed to process loan. Please try again.';
        
        if (error.response?.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response?.status === 400) {
          errorMessage = error.response?.data?.message || 'Invalid loan request.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setLoanError(errorMessage);
      } finally {
        setLoanLoading(false);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle loan repayment
  const handleLoanRepay = async () => {
    const amount = parseFloat(repayAmount);
    if (amount > 0 && amount <= gameState.loan) {
      setRepayLoading(true);
      setRepayError(null);
      
      try {
        const userId = user._id || localStorage.getItem('userId');
        const response = await transactionService.repayLoan(userId, amount);
        
        if (response.success) {
          await loadPortfolioData();
          setShowRepayModal(false);
          setRepayAmount('');
          if (gameState.loan - amount <= 0) {
            setAcceptedLoan(false);
          }
        } else {
          throw new Error(response.message || 'Repayment failed');
        }
      } catch (error) {
        console.error('Loan repayment error:', error);
        setRepayError(error.message || 'Failed to repay loan. Please try again.');
      } finally {
        setRepayLoading(false);
      }
    }
  };

  // Handle deposit to savings
  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (amount > 0) {
      setDepositLoading(true);
      setDepositError(null);
      
      try {
        const userId = user._id || localStorage.getItem('userId');
        const response = await transactionService.openDeposit(userId, amount);
        
        if (response.success) {
          await loadPortfolioData();
          setShowDepositModal(false);
          setDepositAmount('');
        } else {
          throw new Error(response.message || 'Deposit failed');
        }
      } catch (error) {
        console.error('Deposit error:', error);
        setDepositError(error.message || 'Failed to deposit. Please try again.');
      } finally {
        setDepositLoading(false);
      }
    }
  };

  // Handle withdrawal from savings
  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (amount > 0 && amount <= gameState.savingsBalance) {
      setWithdrawLoading(true);
      setWithdrawError(null);
      
      try {
        const userId = user._id || localStorage.getItem('userId');
        const response = await transactionService.closeDeposit(userId, amount);
        
        if (response.success) {
          await loadPortfolioData();
          setShowWithdrawModal(false);
          setWithdrawAmount('');
        } else {
          throw new Error(response.message || 'Withdrawal failed');
        }
      } catch (error) {
        console.error('Withdrawal error:', error);
        setWithdrawError(error.message || 'Failed to withdraw. Please try again.');
      } finally {
        setWithdrawLoading(false);
      }
    }
  };

  // Investment options from GAME.MD
  const investmentOptions = [
    {
      name: t('dashboard:investmentOpportunities.assetTypes.equitySecurities.name'),
      type: 'stocks',
      description: t('dashboard:investmentOpportunities.assetTypes.equitySecurities.description'),
      examples: t('dashboard:investmentOpportunities.assetTypes.equitySecurities.examples'),
      learningFocus: t('dashboard:investmentOpportunities.assetTypes.equitySecurities.learningFocus'),
      riskLevel: t('dashboard:investmentOpportunities.assetTypes.equitySecurities.riskLevel'),
      category: t('dashboard:investmentOpportunities.assetTypes.equitySecurities.category')
    },
    {
      name: t('dashboard:investmentOpportunities.assetTypes.indexFunds.name'),
      type: 'etfs',
      description: t('dashboard:investmentOpportunities.assetTypes.indexFunds.description'),
      examples: t('dashboard:investmentOpportunities.assetTypes.indexFunds.examples'),
      learningFocus: t('dashboard:investmentOpportunities.assetTypes.indexFunds.learningFocus'),
      riskLevel: t('dashboard:investmentOpportunities.assetTypes.indexFunds.riskLevel'),
      category: t('dashboard:investmentOpportunities.assetTypes.indexFunds.category')
    },
    {
      name: t('dashboard:investmentOpportunities.assetTypes.digitalAssetEtfs.name'),
      type: 'crypto',
      description: t('dashboard:investmentOpportunities.assetTypes.digitalAssetEtfs.description'),
      examples: t('dashboard:investmentOpportunities.assetTypes.digitalAssetEtfs.examples'),
      learningFocus: t('dashboard:investmentOpportunities.assetTypes.digitalAssetEtfs.learningFocus'),
      riskLevel: t('dashboard:investmentOpportunities.assetTypes.digitalAssetEtfs.riskLevel'),
      category: t('dashboard:investmentOpportunities.assetTypes.digitalAssetEtfs.category')
    },
    {
      name: t('dashboard:investmentOpportunities.assetTypes.fixedIncome.name'),
      type: 'bonds',
      description: t('dashboard:investmentOpportunities.assetTypes.fixedIncome.description'),
      examples: t('dashboard:investmentOpportunities.assetTypes.fixedIncome.examples'),
      learningFocus: t('dashboard:investmentOpportunities.assetTypes.fixedIncome.learningFocus'),
      riskLevel: t('dashboard:investmentOpportunities.assetTypes.fixedIncome.riskLevel'),
      category: t('dashboard:investmentOpportunities.assetTypes.fixedIncome.category')
    },
    {
      name: t('dashboard:investmentOpportunities.assetTypes.groupRankings.name'),
      type: 'groups',
      description: t('dashboard:investmentOpportunities.assetTypes.groupRankings.description'),
      examples: t('dashboard:investmentOpportunities.assetTypes.groupRankings.examples'),
      learningFocus: t('dashboard:investmentOpportunities.assetTypes.groupRankings.learningFocus'),
      riskLevel: t('dashboard:investmentOpportunities.assetTypes.groupRankings.riskLevel'),
      category: t('dashboard:investmentOpportunities.assetTypes.groupRankings.category')
    }
  ];


  const getLevelText = () => {
    switch(gameState.experienceLevel) {
      case 1: return 'BEGINNER INVESTOR';
      case 2: return 'APPRENTICE TRADER';
      case 3: return 'EXPERT ANALYST';
      case 4: return 'MASTER INVESTOR';
      default: return 'LEGENDARY TRADER';
    }
  };

  const calculateNetWorth = () => {
    return gameState.cash + gameState.savingsBalance + gameState.totalInvested - gameState.loan;
  };

  // Learning slides for kids

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dashboard-container"
      data-testid="dashboard"
      data-loading={portfolioLoading}
      data-mobile={isMobile}
      style={{ overflow: 'visible' }}
    >
      {/* Trade Popup - Hidden on mobile */}
      {showTradePopup && !isMobile && (
        <div
          data-testid="trade-popup-modal"
          style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#FFFFFF',
          border: '4px solid #000000',
          borderRadius: '0',
          padding: '30px 40px',
          boxShadow: '8px 8px 0px #333333',
          zIndex: 1000,
          maxWidth: '400px',
          width: '90%'
        }}>
          <button
            onClick={() => setShowTradePopup(false)}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              background: '#DC2626',
              color: '#FFFFFF',
              border: '3px solid #000000',
              borderRadius: '0',
              width: '40px',
              height: '40px',
              fontSize: '20px',
              fontWeight: '900',
              cursor: 'pointer',
              boxShadow: '3px 3px 0px #333333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.1s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translate(-2px, -2px)';
              e.target.style.boxShadow = '5px 5px 0px #333333';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translate(0, 0)';
              e.target.style.boxShadow = '3px 3px 0px #333333';
            }}
          >
            ✕
          </button>
          
          <div style={{
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '900',
              color: '#dc2626',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '20px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontFamily: "'Arial Black', 'Impact', 'Franklin Gothic Medium', 'Arial Narrow', sans-serif",
              backgroundColor: '#FEF2F2',
              padding: '10px 20px',
              borderRadius: '8px',
              border: '2px solid #dc2626'
            }}>
              📉 TO TRADE GO DOWN 📈
            </h2>
            <p style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#333333',
              marginBottom: '10px'
            }}>
              Scroll down to find trading options
            </p>
            <div className="bounce-animation" style={{
              fontSize: '40px'
            }}>
              ⬇️
            </div>
            <div style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              fontSize: '48px',
              transform: 'rotate(45deg)',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              ➡️
            </div>
            <p style={{
              position: 'absolute',
              bottom: '10px',
              right: '80px',
              fontSize: '14px',
              fontWeight: '700',
              color: '#dc2626',
              whiteSpace: 'nowrap'
            }}>
              {t('dashboard:popup.askAiAssistant')}
            </p>
          </div>
        </div>
      )}
      
      {/* Popup Overlay - Hidden on mobile */}
      {showTradePopup && !isMobile && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
          onClick={() => setShowTradePopup(false)}
        />
      )}

      {/* MAIN CONTENT */}
      <div
        className="w-full"
        data-testid="dashboard-content"
        style={{ marginTop: isMobile ? '80px' : '90px' }}
      >
        {/* TOP BAR - GAME STATISTICS */}
        <MobileTopBarClean
          data-testid="mobile-top-bar"
          stats={(() => {
            const stats = getHeaderStats();
            return {
              ...stats,
              netWorth: stats.netWorth || 0
            };
          })()}
        />

        {/* AI MODE SWITCH - Floating below top bar */}
        <AIModeSwitch
          data-testid="ai-mode-switch"
          style={{
            position: 'fixed',
            top: isMobile ? '95px' : '75px',
            left: isMobile ? '16px' : '32px',
            zIndex: 999
          }}
        />

        {/* DASHBOARD CONTENT */}
        <main
          className="px-4 pb-8 pt-16 sm:px-6 lg:px-8 max-w-7xl mx-auto relative"
          data-testid="dashboard-main"
        >
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-10 -right-10 w-72 h-72 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 -left-20 w-96 h-96 bg-gradient-to-br from-indigo-200/20 to-blue-200/20 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">

          {/* FINANCIAL OVERVIEW */}
          <section
            className="financial-overview grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8"
            data-testid="financial-overview-section"
          >
            <div
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg border border-green-200 p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden group order-2 md:order-1"
              data-testid="cash-balance-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-emerald-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4" style={{ minHeight: '60px' }}>
                  <div className="text-xs font-bold text-green-700 uppercase tracking-wide" style={{ flex: 1, paddingRight: '16px' }}>{t('dashboard:cards.cashBalance.title')}</div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#000000',
                    border: '2px solid #FFFFFF',
                    borderRadius: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: 'rotate(-5deg)',
                    boxShadow: '3px 3px 0px #333333',
                    flexShrink: 0
                  }}>
                    <span style={{
                      color: '#D72638',
                      fontSize: '20px',
                      fontWeight: '900'
                    }}>$</span>
                  </div>
                </div>
                <div className="text-3xl font-bold text-green-800 mb-3">
                  <span style={{color: '#D72638'}}>$</span>{gameState.cash.toLocaleString()}
                </div>
                <div className="text-sm text-green-600 leading-relaxed">
                  {t('dashboard:cards.cashBalance.description')}
                </div>
              </div>
            </div>

            <div
              className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl shadow-lg border border-blue-200 p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden group order-3 md:order-2"
              data-testid="savings-account-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4" style={{ minHeight: '60px' }}>
                  <div className="text-xs font-bold text-blue-700 uppercase tracking-wide" style={{ flex: 1, paddingRight: '16px' }}>{t('dashboard:cards.savingsAccount.title')}</div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#000000',
                    border: '2px solid #FFFFFF',
                    borderRadius: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: 'rotate(-5deg)',
                    boxShadow: '3px 3px 0px #333333',
                    flexShrink: 0
                  }}>
                    <span style={{
                      color: '#D72638',
                      fontSize: '20px',
                      fontWeight: '900'
                    }}>S</span>
                  </div>
                </div>
                <div className="text-3xl font-bold text-blue-800 mb-3">
                  <span style={{color: '#D72638'}}>$</span>{gameState.savingsBalance.toLocaleString()}
                </div>
                <div className="text-sm text-blue-600 leading-relaxed mb-4">
                  {t('dashboard:cards.savingsAccount.description')} {t('dashboard:cards.savingsAccount.dailyEarning')} +<span style={{color: '#D72638'}}>$</span>{(gameState.savingsBalance * gameState.savingsInterestRate).toFixed(2)}
                </div>
                <div className="flex gap-2">
                  <button 
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Deposit button clicked');
                      setShowDepositModal(true);
                    }}
                    disabled={portfolioLoading}
                    title={portfolioLoading ? 'Loading...' : 'Deposit money to savings account'}
                  >
                    {portfolioLoading ? 'Loading...' : t('dashboard:cards.savingsAccount.actions.deposit')}
                  </button>
                  <button 
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowWithdrawModal(true);
                    }}
                    disabled={portfolioLoading || gameState.savingsBalance <= 0}
                    title={portfolioLoading ? 'Loading...' : `Available savings: $${gameState.savingsBalance?.toFixed(2) || 0}`}
                  >
                    {portfolioLoading ? 'Loading...' : t('dashboard:cards.savingsAccount.actions.withdraw')}
                  </button>
                </div>
              </div>
            </div>

            <div
              className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg border border-purple-200 p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden group order-1 md:order-3"
              data-testid="portfolio-value-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 to-pink-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4" style={{ minHeight: '60px' }}>
                  <div className="text-xs font-bold text-purple-700 uppercase tracking-wide" style={{ flex: 1, paddingRight: '16px' }}>{t('dashboard:cards.portfolioValue.title')}</div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#000000',
                    border: '2px solid #FFFFFF',
                    borderRadius: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: 'rotate(-5deg)',
                    boxShadow: '3px 3px 0px #333333',
                    flexShrink: 0
                  }}>
                    <span style={{
                      color: '#D72638',
                      fontSize: '20px',
                      fontWeight: '900'
                    }}>P</span>
                  </div>
                </div>
                <div className="text-3xl font-bold text-purple-800 mb-3">
                  <span style={{color: '#D72638'}}>$</span>{gameState.totalInvested.toLocaleString()}
                </div>
                <div className="text-sm text-purple-600 leading-relaxed mb-4">
                  {t('dashboard:cards.portfolioValue.description')}
                </div>
                <div className="flex gap-2">
                  <button 
                    className="flex-1 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                    style={{
                      background: '#DC2626',
                      border: '2px solid #000000',
                      borderRadius: '8px',
                      fontWeight: '900',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#B91C1C';
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#DC2626';
                      e.target.style.transform = 'scale(1)';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Positions button clicked');
                      // Trigger the FULL FINANCE button click to open financial dashboard
                      // Store the desired tab in localStorage so the component can read it
                      localStorage.setItem('financialDashboardActiveTab', '1'); // 1 for positions tab
                      
                      // Find and click the FULL FINANCE button in the mobile header
                      const buttons = document.querySelectorAll('button');
                      let fullFinanceButton = null;
                      
                      buttons.forEach(btn => {
                        if (btn.textContent && btn.textContent.includes('FULL') && btn.textContent.includes('FINANCE')) {
                          fullFinanceButton = btn;
                        }
                      });
                      
                      if (fullFinanceButton) {
                        fullFinanceButton.click();
                      } else {
                        console.log('Could not find FULL FINANCE button');
                      }
                    }}
                    disabled={portfolioLoading}
                    title={portfolioLoading ? 'Loading...' : 'View your positions'}
                  >
                    {portfolioLoading ? 'Loading...' : t('dashboard:cards.portfolioValue.actions.positions')}
                  </button>
                  <button 
                    className="flex-1 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                    style={{
                      background: '#DC2626',
                      border: '2px solid #000000',
                      borderRadius: '8px',
                      fontWeight: '900',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#B91C1C';
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#DC2626';
                      e.target.style.transform = 'scale(1)';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Trade button clicked');
                      
                      // Scroll to Asset Classes section
                      const assetClassesSection = document.getElementById('asset-classes-section');
                      if (assetClassesSection) {
                        // Calculate the position to scroll to, accounting for header height
                        // Different offsets for desktop vs mobile
                        const isMobile = window.innerWidth <= 768;
                        const headerHeight = isMobile ? 80 : 120; // Adjusted offset to position at asset classes
                        const elementPosition = assetClassesSection.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                        
                        // Smooth scroll to the section
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      } else {
                        console.log('Could not find Asset Classes section');
                      }
                    }}
                    disabled={portfolioLoading}
                    title={portfolioLoading ? 'Loading...' : 'Start trading'}
                  >
                    {portfolioLoading ? 'Loading...' : t('dashboard:cards.portfolioValue.actions.trade')}
                  </button>
                </div>
              </div>
            </div>

            {/* CREDIT AVAILABLE - Show remaining credit */}
            {((gameState.loan || 0) < 1000) && (
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl shadow-lg border border-orange-200 p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden group" style={{
                background: 'linear-gradient(135deg, #fff7ed 0%, #fefce8 100%)',
                border: '3px solid #f59e0b',
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)'
              }}>
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-yellow-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4" style={{ minHeight: '60px' }}>
                    <div className="text-xs font-bold text-orange-700 uppercase tracking-wide" style={{ flex: 1, paddingRight: '16px' }}>{t('dashboard:cards.creditAvailable.title')}</div>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: '#000000',
                      border: '2px solid #FFFFFF',
                      borderRadius: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: 'rotate(-5deg)',
                      boxShadow: '3px 3px 0px #333333',
                      flexShrink: 0
                    }}>
                      <span style={{
                        color: '#D72638',
                        fontSize: '20px',
                        fontWeight: '900'
                      }}>C</span>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-orange-800 mb-3">
                    <span style={{color: '#D72638'}}>$</span>{(1000 - (gameState.loan || 0)).toLocaleString()}
                  </div>
                  <div className="text-sm text-orange-600 leading-relaxed mb-4">
                    {gameState.loan > 0 ?
                      <>{t('dashboard:cards.creditAvailable.descriptionWithLoan')} <span style={{color: '#D72638'}}>$</span>{gameState.loan.toFixed(2)}</> :
                      t('dashboard:cards.creditAvailable.description')
                    }
                    <br/>{t('dashboard:cards.creditAvailable.noPenalty')}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      className="flex-1 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLoanModal(true);
                      }}
                      disabled={portfolioLoading}
                      title={portfolioLoading ? 'Loading...' : `Apply for additional loan (max: $${(1000 - (gameState.loan || 0)).toFixed(2)})`}
                    >
                      {portfolioLoading ? 'Loading...' : (gameState.loan > 0 ? t('dashboard:cards.creditAvailable.actions.getMoreCredit') : t('dashboard:cards.creditAvailable.actions.applyForLoan'))}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ACTIVE LOAN - Show current loan with repayment option */}
            {gameState.loan > 0 && (
              <div
                className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl shadow-lg border border-red-200 p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden group order-4 md:order-4"
                data-testid="active-loan-card"
                style={{
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fdf2f8 100%)',
                  border: '3px solid #dc2626',
                  boxShadow: '0 4px 20px rgba(220, 38, 56, 0.15)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-400/10 to-pink-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4" style={{ minHeight: '60px' }}>
                    <div className="text-xs font-bold text-red-700 uppercase tracking-wide" style={{
                      flex: 1,
                      paddingRight: '16px',
                      color: '#000000',
                      fontWeight: '900',
                      fontSize: '14px',
                      textShadow: '2px 2px 0px #ffffff, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000, 1px 1px 0px #000000'
                    }}>{t('dashboard:cards.activeLoan.title')}</div>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: '#000000',
                      border: '2px solid #FFFFFF',
                      borderRadius: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: 'rotate(-5deg)',
                      boxShadow: '3px 3px 0px #333333',
                      flexShrink: 0
                    }}>
                      <span style={{
                        color: '#D72638',
                        fontSize: '20px',
                        fontWeight: '900'
                      }}>L</span>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-red-800 mb-3" style={{
                    color: '#991b1b',
                    fontSize: '32px',
                    fontWeight: '900',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                  }}>-<span style={{color: '#D72638'}}>$</span>{gameState.loan.toLocaleString()}</div>
                  <div className="text-sm text-red-600 leading-relaxed mb-4" style={{
                    color: '#dc2626',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {t('dashboard:cards.activeLoan.interestInfo')} <span style={{color: '#D72638'}}>$</span>{(gameState.loan * gameState.loanInterestRate).toFixed(2)}.
                    <br/>{t('dashboard:cards.activeLoan.remainingCredit')} <span style={{color: '#D72638'}}>$</span>{(1000 - gameState.loan).toFixed(2)}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowRepayModal(true);
                      }}
                      disabled={portfolioLoading || gameState.cash <= 0}
                      title={portfolioLoading ? 'Loading...' : `Available cash: $${gameState.cash?.toFixed(2) || 0}`}
                    >
                      {portfolioLoading ? 'Loading...' : t('dashboard:cards.activeLoan.actions.repayLoan')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* GIDEON BANNER - Above Investment Opportunities */}
          {!hasCompletedOnboarding && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '1.5rem',
              position: 'relative',
              zIndex: 10,
              perspective: '1000px'
            }}>
              <div
                className="dollar-bill-container"
                onClick={() => {
                  if (!isInitialLoading && !hasCompletedOnboarding) {
                    setShowGideonAssessment(true);
                  }
                }}
                style={{
                  width: isMobile ? '280px' : '350px',
                  height: isMobile ? '280px' : '350px',
                  position: 'relative',
                  cursor: 'pointer',
                  borderRadius: '16px',
                  border: '4px solid #DC2626',
                  boxShadow: '0 8px 24px rgba(220, 38, 38, 0.3)',
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1b1b 50%, #1a1a1a 100%)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(220, 38, 38, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(220, 38, 38, 0.3)';
                  }
                }}
              >
                <img
                  src={require('../assets/images/gideon-bull-banner.png')}
                  alt="Gideon Bull Banner - Click to Start Assessment"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '20px',
                    pointerEvents: 'none'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(220, 38, 38, 0.95)',
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                  animation: 'bounce 2s infinite'
                }}>
                  CLICK
                </div>
              </div>
            </div>
          )}

          {/* INVESTMENT OPPORTUNITIES */}
          <section className="investment-opportunities mb-6" style={{ overflow: 'visible' }}>
            <div className="text-center mb-12" style={{ position: 'relative', overflow: 'visible' }}>

              {/* Side decorative elements */}
              <div style={{
                position: 'absolute',
                top: '60px',
                left: '20%',
                transform: 'rotate(-15deg)',
                width: '30px',
                height: '30px',
                background: '#FFFFFF',
                border: '2px solid #000000',
                boxShadow: '3px 3px 0px #333333',
                zIndex: 5
              }}></div>

              <div style={{
                position: 'absolute',
                top: '60px',
                right: '20%',
                transform: 'rotate(15deg)',
                width: '30px',
                height: '30px',
                background: '#FFFFFF',
                border: '2px solid #000000',
                boxShadow: '3px 3px 0px #333333',
                zIndex: 5
              }}></div>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full text-sm font-semibold text-blue-800 mb-4">
                <img 
                  src={require('../assets/images/red-magnifying-glass.png')} 
                  alt="Magnifying Glass"
                  style={{
                    width: '48px',
                    height: '48px',
                    objectFit: 'contain',
                    filter: 'contrast(1.2) saturate(1.3) drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                  }}
                />
                <span>{t('dashboard:investmentOpportunities.title')}</span>
              </div>
              
              <div style={{
                fontSize: '1.125rem',
                color: '#666666',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: 'center',
                maxWidth: '2xl',
                margin: '0 auto',
                lineHeight: '1.4',
                position: 'relative',
                zIndex: 10
              }}>
                {t('dashboard:investmentOpportunities.subtitle')}
              </div>
            </div>
          </section>

          {/* Asset Classes Grid */}
          <div id="asset-classes-section" className={`grid gap-6 mb-8 ${isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4'}`}>
            {investmentOptions.filter(option => option.type !== 'groups').map((option) => (
              <div 
                key={option.name}
                className={`investment-option-card relative rounded-3xl shadow-xl border-2 ${isMobile ? 'p-4' : 'p-8'} hover:shadow-2xl transition-all duration-300 cursor-pointer group overflow-hidden transform hover:scale-105 ${
                  option.type === 'stocks' ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' :
                  option.type === 'etfs' ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200' :
                  option.type === 'crypto' ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200' :
                  option.type === 'groups' ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200' :
                  'bg-gradient-to-br from-orange-50 to-yellow-50 border-orange-200'
                }`}
                onClick={() => {
                  // Navigate to asset page
                  if (option.type === 'stocks') {
                    navigate('/stocks');
                  } else if (option.type === 'etfs') {
                    navigate('/etf-indices');
                  } else if (option.type === 'crypto') {
                    navigate('/crypto-etfs');
                  } else if (option.type === 'bonds') {
                    navigate('/bond-etfs');
                  } else if (option.type === 'groups') {
                    navigate('/groups');
                  } else {
                    navigate('/assets', { state: { category: option.type } });
                  }
                }}
              >
                {/* Animated background effect */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                  option.type === 'stocks' ? 'bg-gradient-to-br from-green-400/20 to-emerald-400/20' :
                  option.type === 'etfs' ? 'bg-gradient-to-br from-blue-400/20 to-indigo-400/20' :
                  option.type === 'crypto' ? 'bg-gradient-to-br from-purple-400/20 to-pink-400/20' :
                  option.type === 'groups' ? 'bg-gradient-to-br from-rose-400/20 to-pink-400/20' :
                  'bg-gradient-to-br from-orange-400/20 to-yellow-400/20'
                }`}></div>

                {/* Category Badge */}
                <div className="absolute top-6 right-6">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                    option.type === 'stocks' ? 'bg-green-100 text-green-800' :
                    option.type === 'etfs' ? 'bg-blue-100 text-blue-800' :
                    option.type === 'crypto' ? 'bg-purple-100 text-purple-800' :
                    option.type === 'groups' ? 'bg-rose-100 text-rose-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {option.category}
                  </span>
                </div>
                
                {/* Risk Level Badge */}
                <div className="absolute top-20 right-6">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold ${
                    option.riskLevel === 'HIGH' ? 'bg-red-100 text-red-800' :
                    option.riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {option.riskLevel} RISK
                  </span>
                </div>
                
                <div className={`relative z-10 ${isMobile ? 'pt-16' : 'pt-24'}`}>

                  {/* Professional Header */}
                  <h3 className={`red-asset-title ${isMobile ? 'text-lg' : 'text-2xl'} font-bold ${isMobile ? 'mb-2' : 'mb-4'} transition-colors ${
                    option.type === 'stocks' ? 'text-green-800 group-hover:text-green-900' :
                    option.type === 'etfs' ? 'text-blue-800 group-hover:text-blue-900' :
                    option.type === 'crypto' ? 'text-purple-800 group-hover:text-purple-900' :
                    'text-orange-800 group-hover:text-orange-900'
                  }`}>
                    {option.name}
                  </h3>

                  {/* Mobile: Simplified description, Desktop: Full content */}
                  {isMobile ? (
                    <>
                      {/* Mobile: Expandable description */}
                      <div className="text-xs text-gray-700 mb-3 leading-relaxed">
                        {expandedAssets.has(option.name) ? (
                          <div className="animate-fadeIn">
                            <p className="mb-2">{option.description}</p>
                            <div className="bg-gray-50 p-2 rounded-lg mb-2">
                              <div className="text-xs font-semibold text-gray-800 mb-1">Examples:</div>
                              <div className="text-xs text-gray-600">{option.examples}</div>
                            </div>
                            <div className="bg-blue-50 p-2 rounded-lg">
                              <div className="text-xs font-semibold text-blue-800 mb-1">What You'll Learn:</div>
                              <div className="text-xs text-blue-700">{option.learningFocus}</div>
                            </div>
                          </div>
                        ) : (
                          <p className="line-clamp-2">
                            {option.description.split('.')[0]}...
                          </p>
                        )}
                        
                        {/* Expand/Collapse Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAssetExpansion(option.name);
                          }}
                          className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center gap-1"
                        >
                          {expandedAssets.has(option.name) ? (
                            <>
                              <span>Show less</span>
                              <span className="transform rotate-180 transition-transform duration-200">▼</span>
                            </>
                          ) : (
                            <>
                              <span>Read more</span>
                              <span className="transition-transform duration-200">▼</span>
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Mobile: Mini performance chart */}
                      <div className="mb-3">
                        <div className="flex items-end gap-0.5 h-6 mb-1">
                          {[3, 5, 4, 6, 8, 5, 7].map((height, index) => (
                            <div
                              key={index}
                              className={`rounded-t-sm flex-1 transition-all duration-300 group-hover:scale-110 ${
                                option.type === 'stocks' ? 'bg-gradient-to-t from-green-400 to-green-600' :
                                option.type === 'etfs' ? 'bg-gradient-to-t from-blue-400 to-blue-600' :
                                option.type === 'crypto' ? 'bg-gradient-to-t from-purple-400 to-purple-600' :
                                'bg-gradient-to-t from-orange-400 to-orange-600'
                              }`}
                              style={{ height: `${height * 3}px` }}
                            ></div>
                          ))}
                        </div>
                        <div className="text-xs text-gray-500 text-center">Trend</div>
                      </div>


                      {/* Mobile: Compact action button */}
                      <button 
                        className="red-asset-button w-full text-sm py-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navigate directly on button click
                          if (option.type === 'stocks') {
                            navigate('/stocks');
                          } else if (option.type === 'etfs') {
                            navigate('/etf-indices');
                          } else if (option.type === 'crypto') {
                            navigate('/crypto-etfs');
                          } else if (option.type === 'bonds') {
                            navigate('/bond-etfs');
                          } else if (option.type === 'groups') {
                            navigate('/groups');
                          } else {
                            navigate('/assets', { state: { category: option.type } });
                          }
                        }}
                      >
                        Explore
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Desktop: Full content as before */}
                      <p className="text-sm text-gray-700 mb-6 leading-relaxed line-clamp-4">{option.description}</p>

                      {/* Data Examples */}
                      <div className={`text-xs mb-6 p-4 rounded-xl font-medium ${
                        option.type === 'stocks' ? 'bg-green-100 text-green-800' :
                        option.type === 'etfs' ? 'bg-blue-100 text-blue-800' :
                        option.type === 'crypto' ? 'bg-purple-100 text-purple-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {option.examples}
                      </div>

                      {/* Learning Focus */}
                      <div className="text-xs text-gray-600 mb-6 font-medium bg-gray-100 p-4 rounded-xl">
                        <strong>Learning:</strong> {option.learningFocus}
                      </div>

                      {/* Enhanced Performance Chart */}
                      <div className="mb-6">
                        <div className="flex items-end gap-1 h-12 mb-2">
                          {[3, 5, 4, 6, 8, 5, 7].map((height, index) => (
                            <div
                              key={index}
                              className={`rounded-t-sm flex-1 transition-all duration-300 group-hover:scale-110 ${
                                option.type === 'stocks' ? 'bg-gradient-to-t from-green-400 to-green-600' :
                                option.type === 'etfs' ? 'bg-gradient-to-t from-blue-400 to-blue-600' :
                                option.type === 'crypto' ? 'bg-gradient-to-t from-purple-400 to-purple-600' :
                                'bg-gradient-to-t from-orange-400 to-orange-600'
                              }`}
                              style={{ height: `${height * 8}px` }}
                            ></div>
                          ))}
                        </div>
                        <div className="text-xs text-gray-500 text-center">Performance Trend</div>
                      </div>

                      {/* Professional Action Button - Red Style matching Portfolio Banner */}
                      <button 
                        className="red-asset-button"
                      >
                        Explore {option.name}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Group Rankings - Horizontal Banner */}
          <div className="mb-8 mt-8">
            {investmentOptions.filter(option => option.type === 'groups').map((option) => (
              <div 
                key={option.name}
                className="investment-option-card relative cursor-pointer group overflow-hidden transform hover:scale-[1.02]"
                style={{
                  background: '#FFFFFF',
                  border: '3px solid #000000',
                  borderRadius: '0',
                  boxShadow: '6px 6px 0px #333333',
                  padding: isMobile ? '1.5rem' : '2rem'
                }}
                onClick={() => navigate('/groups')}
              >
                {/* Animated background effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-rose-400/20 to-pink-400/20"></div>
                
                {/* Category Badge - Top Right */}
                <div className="absolute top-6 right-6">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800">
                    {option.category}
                  </span>
                </div>
                
                {/* Risk Level Badge - Below Category */}
                <div className="absolute top-16 right-6">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-blue-100 text-blue-800">
                    {option.riskLevel}
                  </span>
                </div>
                
                <div className="relative z-10 flex items-center gap-8">
                  {/* Left side - Title and Description */}
                  <div className="flex-1">
                    <h3 className={`red-asset-title ${isMobile ? 'text-xl' : 'text-3xl'} font-bold mb-4 text-rose-800 group-hover:text-rose-900`}>
                      {option.name}
                    </h3>
                    <p className={`${isMobile ? 'text-sm' : 'text-base'} text-gray-700 mb-4 leading-relaxed`}>
                      {option.description}
                    </p>
                    <div className={`text-sm mb-4 p-3 rounded-xl font-medium bg-rose-100 text-rose-800`}>
                      {option.examples}
                    </div>
                  </div>
                  
                  {/* Right side - CTA */}
                  <div className="flex flex-col items-center justify-center">
                    <img src={chessKingIcon} alt="Competition" className="w-32 h-32 mb-4 object-contain bg-white rounded-lg p-2" />
                    <button 
                      className="red-asset-button px-8 py-3 text-lg font-bold"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/groups');
                      }}
                    >
                      COMPETE NOW
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* EDUCATIONAL GAME INTERFACE - REDESIGNED TO MATCH SCREENSHOT */}
          <section className={`educational-game-section ${isMobile ? 'mb-6' : 'mb-12'}`} style={{
            background: '#FFFFFF',
            border: '3px solid #D72638',
            borderRadius: '16px',
            padding: isMobile ? '24px' : '32px',
            margin: '0 auto',
            maxWidth: '800px',
            boxShadow: '8px 8px 0px #333333'
          }}>
            {/* Header - Remove to match screenshot */}

            {/* Top Grid - D and S Cards */}
            <div className={`grid grid-cols-2 gap-4 mb-6`}>
              {/* D Card - Game Day */}
              <div className="text-center" style={{
                background: '#FFFFFF',
                border: '3px solid #D72638',
                borderRadius: '12px',
                padding: isMobile ? '20px' : '24px',
                boxShadow: '4px 4px 0px #D72638'
              }}>
                <div style={{
                  width: isMobile ? '60px' : '80px',
                  height: isMobile ? '60px' : '80px',
                  background: '#D72638',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: '#FFFFFF',
                  fontSize: isMobile ? '32px' : '48px',
                  fontWeight: '900'
                }}>
                  D
                </div>
                <div style={{
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '900',
                  color: '#D72638',
                  textTransform: 'uppercase',
                  marginBottom: '8px'
                }}>
                  Game Day
                </div>
                <div style={{
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: '900',
                  color: '#000000',
                  marginBottom: '8px'
                }}>
                  {gameState.gameDay}
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '700',
                  color: '#D72638',
                  textTransform: 'uppercase'
                }}>
                  Days of Learning
                </div>
              </div>

              {/* S Card - Safety Level */}
              <div className="text-center" style={{
                background: '#FFFFFF',
                border: '3px solid #D72638',
                borderRadius: '12px',
                padding: isMobile ? '20px' : '24px',
                boxShadow: '4px 4px 0px #D72638'
              }}>
                <div style={{
                  width: isMobile ? '60px' : '80px',
                  height: isMobile ? '60px' : '80px',
                  background: '#D72638',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: '#FFFFFF',
                  fontSize: isMobile ? '32px' : '48px',
                  fontWeight: '900'
                }}>
                  S
                </div>
                <div style={{
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '900',
                  color: '#D72638',
                  textTransform: 'uppercase',
                  marginBottom: '8px'
                }}>
                  Safety Level
                </div>
                <div style={{
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: '900',
                  color: '#000000',
                  marginBottom: '8px'
                }}>
                  100%
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '700',
                  color: '#D72638',
                  textTransform: 'uppercase'
                }}>
                  Virtual Money Only
                </div>
              </div>
            </div>

          </section>
          </div>
        </main>
      </div>

      {/* LOAN MODAL */}
      {showLoanModal && (
        <div
          data-testid="loan-modal-overlay"
          onClick={() => setShowLoanModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div
            data-testid="loan-modal"
            onClick={e => e.stopPropagation()}
            style={{
            background: '#FFFFFF',
            border: '2px solid #000000',
            boxShadow: '6px 6px 0px #000000',
            maxWidth: '420px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          }}>
            <div
              onClick={() => setShowLoanModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '28px',
                height: '28px',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '24px',
                color: '#666666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '300',
                lineHeight: '1',
                zIndex: '10'
              }}
            >
              ×
            </div>

            {/* Header Section */}
            <div style={{
              padding: '20px 50px 16px 24px',
              background: '#000000',
              color: '#FFFFFF',
              position: 'relative'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                color: '#FFFFFF',
                margin: '0 0 6px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Investment Loan
              </h3>
              <div
                onClick={() => {
                  setShowLoanModal(false);
                  const prompt = encodeURIComponent("What is a loan and what can you tell me about my loan options?");
                  navigate(`/chat?prompt=${prompt}`);
                }}
                style={{
                  display: 'inline-block',
                  cursor: 'pointer',
                  background: 'rgba(255,255,255,0.15)',
                  padding: '4px 8px',
                  marginTop: '4px',
                  borderRadius: '4px'
                }}
                title="Ask AI about loans"
              >
                <img src={gideonBullIcon} alt="Ask Gideon" style={{ height: '20px', width: 'auto', display: 'block' }} />
              </div>
            </div>

            {/* Loan Status */}
            {gameState.loan > 0 && (
              <div style={{
                padding: '16px 24px',
                background: '#F5F5F5',
                borderBottom: '1px solid #E5E5E5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#666666', fontWeight: '600', textTransform: 'uppercase', marginBottom: '2px' }}>Current Loan</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#DC2626' }}>${gameState.loan.toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', color: '#666666', fontWeight: '600', textTransform: 'uppercase', marginBottom: '2px' }}>Available Credit</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>${(1000 - gameState.loan).toFixed(2)}</div>
                </div>
              </div>
            )}

            {/* Terms Grid */}
            <div style={{
              padding: '20px 24px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              borderBottom: '1px solid #E5E5E5'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', color: '#888888', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Interest</div>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#000000' }}>4%</div>
                <div style={{ fontSize: '0.7rem', color: '#888888' }}>Annual</div>
              </div>
              <div style={{ textAlign: 'center', borderLeft: '1px solid #E5E5E5', borderRight: '1px solid #E5E5E5' }}>
                <div style={{ fontSize: '0.7rem', color: '#888888', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Compounding</div>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#000000' }}>Daily</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', color: '#888888', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Early Repay</div>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#059669' }}>No Fee</div>
              </div>
            </div>
            
            {/* Content Section */}
            <div style={{ padding: '20px 24px' }}>
              {loanError && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#DC2626',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {loanError}
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  color: '#000000',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Loan Amount
                </label>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '2px solid #000000',
                  background: '#FFFFFF'
                }}>
                  <span style={{
                    padding: '12px 14px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#666666',
                    background: '#F5F5F5',
                    borderRight: '2px solid #000000'
                  }}>$</span>
                  <input
                    type="number"
                    placeholder={`1 - ${(1000 - (gameState.loan || 0)).toFixed(0)}`}
                    min="1"
                    max={1000 - (gameState.loan || 0)}
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#000000',
                      backgroundColor: '#FFFFFF',
                      padding: '12px 14px',
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                    }}
                  />
                </div>

                <div style={{
                  fontSize: '0.75rem',
                  color: '#888888',
                  marginTop: '6px'
                }}>
                  {gameState.loan > 0 ? (
                    <>Max available: ${(1000 - gameState.loan).toFixed(2)}</>
                  ) : (
                    <>Maximum: $1,000</>
                  )}
                </div>
              </div>
              
              {loanAmount && parseFloat(loanAmount) > 0 && (
                <div style={{
                  background: '#F5F5F5',
                  border: '1px solid #E5E5E5',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    color: '#666666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px'
                  }}>Loan Summary</div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.875rem', color: '#666666' }}>Daily Interest</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#000000' }}>
                      ${(parseFloat(loanAmount) * gameState.loanInterestRate).toFixed(4)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: '#666666' }}>Annual Cost (4%)</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#000000' }}>
                      ${(parseFloat(loanAmount) * 0.04).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <button
                  onClick={() => setShowLoanModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #000000',
                    background: '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#000000',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLoanAccept}
                  disabled={!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > (1000 - (gameState.loan || 0)) || loanLoading}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    background: (!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > (1000 - (gameState.loan || 0)) || loanLoading)
                      ? '#CCCCCC'
                      : '#000000',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#FFFFFF',
                    cursor: (!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > (1000 - (gameState.loan || 0)) || loanLoading)
                      ? 'not-allowed'
                      : 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    boxShadow: (!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > (1000 - (gameState.loan || 0)) || loanLoading)
                      ? 'none'
                      : '4px 4px 0px #000000'
                  }}
                >
                  {loanLoading ? 'Processing...' : 'Get Loan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LOAN REPAYMENT MODAL */}
      {showRepayModal && (
        <div
          data-testid="repay-modal-overlay"
          onClick={() => setShowRepayModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div
            data-testid="repay-modal"
            onClick={e => e.stopPropagation()}
            style={{
            background: '#FFFFFF',
            border: '2px solid #000000',
            boxShadow: '6px 6px 0px #000000',
            maxWidth: '380px',
            width: '100%',
            position: 'relative',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          }}>
            <div
              onClick={() => setShowRepayModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '24px',
                height: '24px',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#999999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '300',
                lineHeight: '1',
                zIndex: '10'
              }}
            >
              ×
            </div>

            <div style={{
              padding: '20px 50px 16px 24px',
              background: '#000000',
              color: '#FFFFFF'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                margin: '0',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>Repay Loan</h3>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {repayError && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#DC2626',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {repayError}
                </div>
              )}

              <div style={{
                padding: '12px 16px',
                background: '#F5F5F5',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '0.875rem', color: '#666666' }}>Current Loan</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#DC2626' }}>${gameState.loan.toFixed(2)}</span>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  color: '#000000',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Repayment Amount
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '2px solid #000000',
                  background: '#FFFFFF'
                }}>
                  <span style={{
                    padding: '12px 14px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#666666',
                    background: '#F5F5F5',
                    borderRight: '2px solid #000000'
                  }}>$</span>
                  <input
                    type="number"
                    value={repayAmount}
                    onChange={(e) => setRepayAmount(e.target.value)}
                    max={gameState.loan}
                    min="0"
                    step="0.01"
                    placeholder={`Max ${gameState.loan.toFixed(2)}`}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#000000',
                      backgroundColor: '#FFFFFF',
                      padding: '12px 14px',
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowRepayModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #000000',
                    background: '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#000000',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLoanRepay}
                  disabled={repayLoading || !repayAmount || parseFloat(repayAmount) <= 0}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    background: (repayLoading || !repayAmount || parseFloat(repayAmount) <= 0) ? '#CCCCCC' : '#059669',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#FFFFFF',
                    cursor: (repayLoading || !repayAmount || parseFloat(repayAmount) <= 0) ? 'not-allowed' : 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {repayLoading ? 'Processing...' : 'Repay'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DEPOSIT MODAL */}
      {showDepositModal && (
        <div
          data-testid="deposit-modal-overlay"
          onClick={() => setShowDepositModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div
            data-testid="deposit-modal"
            onClick={e => e.stopPropagation()}
            style={{
            background: '#FFFFFF',
            border: '2px solid #000000',
            boxShadow: '6px 6px 0px #000000',
            maxWidth: '380px',
            width: '100%',
            position: 'relative',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          }}>
            <div
              onClick={() => setShowDepositModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '24px',
                height: '24px',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#999999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '300',
                lineHeight: '1',
                zIndex: '10'
              }}
            >
              ×
            </div>

            <div style={{
              padding: '20px 50px 16px 24px',
              background: '#000000',
              color: '#FFFFFF'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                margin: '0 0 6px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>Deposit to Savings</h3>
              <div
                onClick={() => {
                  setShowDepositModal(false);
                  const prompt = encodeURIComponent("What is a savings account and why should I save money?");
                  navigate(`/chat?prompt=${prompt}`);
                }}
                style={{
                  display: 'inline-block',
                  cursor: 'pointer',
                  background: 'rgba(255,255,255,0.15)',
                  padding: '4px 8px',
                  marginTop: '4px',
                  borderRadius: '4px'
                }}
                title="Ask AI about savings"
              >
                <img src={gideonBullIcon} alt="Ask Gideon" style={{ height: '20px', width: 'auto', display: 'block' }} />
              </div>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {depositError && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#DC2626',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {depositError}
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  color: '#000000',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Deposit Amount
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '2px solid #000000',
                  background: '#FFFFFF'
                }}>
                  <span style={{
                    padding: '12px 14px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#666666',
                    background: '#F5F5F5',
                    borderRight: '2px solid #000000'
                  }}>$</span>
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#000000',
                      backgroundColor: '#FFFFFF',
                      padding: '12px 14px',
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowDepositModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #000000',
                    background: '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#000000',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeposit}
                  disabled={depositLoading || !depositAmount || parseFloat(depositAmount) <= 0}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    background: (depositLoading || !depositAmount || parseFloat(depositAmount) <= 0) ? '#CCCCCC' : '#059669',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#FFFFFF',
                    cursor: (depositLoading || !depositAmount || parseFloat(depositAmount) <= 0) ? 'not-allowed' : 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {depositLoading ? 'Processing...' : t('dashboard:cards.savingsAccount.actions.deposit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WITHDRAW MODAL */}
      {showWithdrawModal && (
        <div
          data-testid="withdraw-modal-overlay"
          onClick={() => setShowWithdrawModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div
            data-testid="withdraw-modal"
            onClick={e => e.stopPropagation()}
            style={{
            background: '#FFFFFF',
            border: '2px solid #000000',
            boxShadow: '6px 6px 0px #000000',
            maxWidth: '380px',
            width: '100%',
            position: 'relative',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          }}>
            <div
              onClick={() => setShowWithdrawModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '24px',
                height: '24px',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#999999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '300',
                lineHeight: '1',
                zIndex: '10'
              }}
            >
              ×
            </div>

            <div style={{
              padding: '20px 50px 16px 24px',
              background: '#000000',
              color: '#FFFFFF'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                margin: '0',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>Withdraw from Savings</h3>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {withdrawError && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#DC2626',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {withdrawError}
                </div>
              )}

              <div style={{
                padding: '12px 16px',
                background: '#F5F5F5',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '0.875rem', color: '#666666' }}>Savings Balance</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#059669' }}>${gameState.savingsBalance.toFixed(2)}</span>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  color: '#000000',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Withdrawal Amount
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '2px solid #000000',
                  background: '#FFFFFF'
                }}>
                  <span style={{
                    padding: '12px 14px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#666666',
                    background: '#F5F5F5',
                    borderRight: '2px solid #000000'
                  }}>$</span>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    max={gameState.savingsBalance}
                    min="0"
                    step="0.01"
                    placeholder={`Max ${gameState.savingsBalance.toFixed(2)}`}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#000000',
                      backgroundColor: '#FFFFFF',
                      padding: '12px 14px',
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowWithdrawModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #000000',
                    background: '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#000000',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    background: (withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) <= 0) ? '#CCCCCC' : '#DC2626',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#FFFFFF',
                    cursor: (withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) <= 0) ? 'not-allowed' : 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {withdrawLoading ? 'Processing...' : t('dashboard:cards.savingsAccount.actions.withdraw')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Floating AI Assistant */}

      {/* Gideon Assessment Modal */}
      <GideonAssessmentModal
        data-testid="gideon-assessment-modal"
        isOpen={showGideonAssessment}
        onClose={() => setShowGideonAssessment(false)}
        onStartAssessment={() => {
          console.log('🎯 Starting Gideon assessment flow...');
          // Start the actual chat/assessment flow
          const onboardingEvent = new CustomEvent('openOnboardingFlow', { 
            detail: { 
              source: 'gideon_assessment_modal',
              context: {
                trigger: 'gideon_bull_banner',
                userIntent: 'personalized_guidance',
                timestamp: new Date().toISOString()
              }
            } 
          });
          window.dispatchEvent(onboardingEvent);
          console.log('🚀 Onboarding event dispatched!');
        }}
        message="I'd love to give you personalized advice that matches your style!"
      />

      {/* Profile Complete Modal - Shows when user already completed assessment */}
      {showProfileCompleteModal && (
        <div className="onboarding-overlay">
          <div className="onboarding-modal">
            {/* Header */}
            <div className="onboarding-header">
              <div className="gideon-avatar">🎯</div>
              <div className="header-content">
                <h2>Assessment Already Complete!</h2>
                <p>You've already completed your investor profile assessment</p>
              </div>
              <button 
                className="close-btn"
                onClick={() => setShowProfileCompleteModal(false)}
              >
                ×
              </button>
            </div>

            {/* Profile Summary */}
            {personalityProfile && (
              <div className="completion-screen">
                <div className="personality-summary">
                  <div className="summary-title">Your Current Investor Profile</div>
                  
                  <div className="profile-badges">
                    <div className={`risk-badge ${personalityProfile.riskTolerance?.category || 'moderate'}`}>
                      <span className="badge-icon">
                        {personalityProfile.riskTolerance?.category === 'conservative' ? '🛡️' :
                         personalityProfile.riskTolerance?.category === 'aggressive' ? '🚀' : '⚖️'}
                      </span>
                      <span className="badge-text">
                        {personalityProfile.riskTolerance?.category || 'Balanced'} Investor
                      </span>
                    </div>

                    {personalityProfile.learningStyle?.pacePreference === 'step-by-step' && (
                      <div className="learning-badge">
                        <span className="badge-icon">📚</span>
                        <span className="badge-text">Step-by-Step Learner</span>
                      </div>
                    )}

                    {personalityProfile.behavioralTraits?.patience > 70 && (
                      <div className="patience-badge">
                        <span className="badge-icon">⏰</span>
                        <span className="badge-text">Patient Investor</span>
                      </div>
                    )}
                  </div>

                  <div className="profile-insights">
                    <h4>Your Personalized Experience:</h4>
                    <ul>
                      <li>🎯 You get personalized investment recommendations</li>
                      <li>🎓 I explain things at your preferred learning pace</li>
                      <li>🔓 All trading features are unlocked for you</li>
                      <li>💡 Ask me anything - I know your investment style!</li>
                    </ul>
                  </div>

                  <div className="completion-actions">
                    <button 
                      className="start-trading-btn"
                      onClick={() => setShowProfileCompleteModal(false)}
                      style={{ marginRight: '10px' }}
                    >
                      Continue Trading 🚀
                    </button>
                    <button 
                      className="reset-profile-btn"
                      onClick={async () => {
                        try {
                          console.log('🔄 Resetting personality profile...');
                          await resetPersonalityProfile();
                          setShowProfileCompleteModal(false);
                          console.log('✅ Profile reset - user can retake assessment');
                        } catch (error) {
                          console.error('❌ Failed to reset profile:', error);
                        }
                      }}
                      style={{
                        background: '#6B7280',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#4B5563'}
                      onMouseOut={(e) => e.target.style.background = '#6B7280'}
                    >
                      Retake Assessment 🔄
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Competition Button - Parallel to AI Assistant on left side */}
      <FloatingCompetitionButton data-testid="floating-competition-button" />

    </div>
  );
};

export default Dashboard;